import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FriendAndPartnerClient, FriendAndPartnerDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new FriendAndPartnerClient(API_BASE_URL, axiosInstance);

// Query hook - Get all friendandpartners for dropdowns (cached 4 hours)
export const useFriendAndPartners = () => {
  return useQuery({
    queryKey: ['friendandpartners'],
    queryFn: async () => {
      const response = await apiClient.searchFriendAndPartners({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useFriendAndPartnerSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchFriendAndPartners({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single friendAndPartner by ID
export const useFriendAndPartnerById = (id: string) => {
  return useQuery({
    queryKey: ['friendandpartners', id],
    queryFn: async () => {
      const response = await apiClient.getFriendAndPartnerById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create friendAndPartner
export const useCreateFriendAndPartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: FriendAndPartnerDTO) => {
      await apiClient.createFriendAndPartner(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friendandpartners'] });
    },
  });
};

// Mutation hook - Update friendAndPartner
export const useUpdateFriendAndPartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FriendAndPartnerDTO }) => {
      await apiClient.updateFriendAndPartner(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friendandpartners'] });
    },
  });
};

// Mutation hook - Delete friendAndPartner
export const useDeleteFriendAndPartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteFriendAndPartner(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friendandpartners'] });
    },
  });
};
